"use client";

import { useEffect, useState } from "react";
import { NeonMusicLoader } from "./neon-music-loader";
import { cn } from "@/lib/utils";

interface PageLoaderProps {
  /** Delay in ms before showing the loader (prevents flash on quick loads) */
  delay?: number;
  /** Optional message to display */
  message?: string;
  /** Size of the loader */
  size?: "sm" | "md" | "lg" | "xl";
  /** Additional class names */
  className?: string;
}

/**
 * A page-level loading component that shows the NeonMusicLoader
 * with an optional delay to prevent flash on quick page loads.
 */
export function PageLoader({
  delay = 150,
  message,
  size = "md",
  className,
}: PageLoaderProps) {
  const [showLoader, setShowLoader] = useState(delay === 0);

  useEffect(() => {
    if (delay === 0) return;
    
    const timer = setTimeout(() => {
      setShowLoader(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  if (!showLoader) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center min-h-[200px] w-full",
        "animate-in fade-in duration-200",
        className
      )}
    >
      <NeonMusicLoader size={size} showLabel={false} />
      {message && (
        <p className="mt-4 text-sm text-muted-foreground animate-pulse">
          {message}
        </p>
      )}
    </div>
  );
}

/**
 * Full-page loading overlay with the music loader.
 * Use this sparingly as it covers the entire viewport.
 */
export function FullPageLoader({
  delay = 200,
  message = "Loading...",
}: {
  delay?: number;
  message?: string;
}) {
  const [showLoader, setShowLoader] = useState(delay === 0);

  useEffect(() => {
    if (delay === 0) return;
    
    const timer = setTimeout(() => {
      setShowLoader(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  if (!showLoader) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="flex flex-col items-center gap-4">
        <NeonMusicLoader size="lg" showLabel={false} />
        <p className="text-sm text-muted-foreground animate-pulse">{message}</p>
      </div>
    </div>
  );
}









